import { SortDirection } from 'constants/SortDirection';
import { isEqual, isNumber } from 'lodash';
import { action, makeObservable, observable, runInAction } from 'mobx';

import { IDataFetchOptions, IGetDataParams } from '../types';

export const MAX_DATA_LENGTH = 1000;
export const DEFAULT_LIMIT = 25;
export const DEFAULT_SKIP = 0;
export const DEFAULT_PAGE_NUMBER = 1;

export interface BaseClassConstructor<T> {
    new (args: any): T;
}

export interface BaseListStoreArgs<T> {
    sortBy?: string;
    sortDirection?: SortDirection;
    defaultFilter?: Record<string, unknown>;
    limit?: number;
    skip?: number;
    reset?: boolean;
    filter?: Record<string, unknown>;
    itemStore: BaseClassConstructor<T>;
    pagination?: boolean;
}

export default class BaseListStore<T> {
    limit = DEFAULT_LIMIT;
    skip = DEFAULT_SKIP;
    sortBy = 'createdOn';
    sortDirection: SortDirection = SortDirection.DESC;
    filter: Record<string, unknown> = { search: '' };
    totalCount = 0;
    count = 0;
    page = DEFAULT_PAGE_NUMBER;
    pageSize = DEFAULT_LIMIT;
    ids: string[] = [];
    selectedIds: Record<string, boolean | undefined> = {};
    data: Map<string, T | undefined> = new Map();
    defaultFilter: Record<string, unknown> = { search: '' };
    ItemStore: BaseClassConstructor<T>;
    pagination: boolean | undefined;
    timesSorted = 0;

    protected get sort(): string {
        return `${this.sortBy} ${this.sortDirection}`;
    }

    public get items(): (T | undefined)[] {
        return this.ids.map((id) => this.data.get(id));
    }

    public get selectedIdList(): string[] {
        return Object.keys(this.selectedIds).filter((k) => this.selectedIds[k]);
    }

    constructor({ sortBy, sortDirection, defaultFilter = { search: '' }, limit = 0, skip = 0, itemStore }: BaseListStoreArgs<T>) {
        makeObservable<BaseListStore<T>, '_updateListState'>(this, {
            sortBy: observable,
            sortDirection: observable,
            filter: observable,
            totalCount: observable,
            count: observable,
            page: observable,
            pageSize: observable,
            ids: observable,
            selectedIds: observable,
            data: observable,
            toggleItem: action,
            selectAll: action,
            deselectAll: action,
            clearData: action,
            clearIds: action,
            clearItem: action,
            clear: action,
            setFilter: action,
            resetFilter: action,
            _updateIds: action,
            _updateData: action,
            _updateListState: action,
            _updateCount: action,
            updatePageSize: action,
            updatePage: action
        });

        if (!itemStore) {
            throw new Error('List stores require an item store');
        }

        this.limit = limit || this.limit;
        this.skip = skip || this.skip;
        this.sortBy = sortBy || this.sortBy;
        this.sortDirection = sortDirection || this.sortDirection;
        this.defaultFilter = defaultFilter || this.filter;
        this.filter = { ...defaultFilter };
        this.ItemStore = itemStore;
    }

    protected _updateListState({
        limit = this.limit,
        skip = this.skip,
        sortBy = this.sortBy,
        sortDirection = this.sortDirection,
        filter = this.filter,
        reset = false
    }: IDataFetchOptions): boolean {
        const newState = {
            limit: limit != null ? limit : this.limit,
            skip: skip != null ? skip : this.skip,
            sortBy: sortBy || this.sortBy,
            sortDirection: sortDirection || this.sortDirection,
            filter: filter || this.filter
        };

        const shouldReset = reset || this.sortBy !== newState.sortBy || this.sortDirection !== newState.sortDirection || !isEqual(this.filter, newState.filter);

        this.sortBy = newState.sortBy;
        this.sortDirection = newState.sortDirection;
        this.filter = newState.filter;

        if (shouldReset) {
            this.limit = DEFAULT_LIMIT;
            this.skip = DEFAULT_SKIP;
        } else {
            this.limit = newState.limit;
            this.skip = newState.skip;
        }

        return shouldReset;
    }

    protected updateStateForCorrectPagination<T>({ args, data, totalCount }: { args: IGetDataParams; data: T[]; totalCount: number }): void {
        const { pageSize, page } = args;

        const shouldReset = this._updateListState(args);

        if (shouldReset) {
            this.clearIds();
        }

        this._updateData(data);
        this._updateIds<T>(data, 0);

        runInAction(() => {
            this.count = data.length;
            this.totalCount = totalCount;

            if (isNumber(page)) {
                this.page = page;
            }

            if (isNumber(pageSize)) {
                this.pageSize = pageSize;
            }
        });
    }

    public findItem(itemId?: string | null): T | undefined {
        if (!itemId) {
            return;
        }

        return this.data.get(itemId);
    }

    setTimesSorted(timesSorted: number): void {
        this.timesSorted = timesSorted;
    }

    toggleItem(id: string): void {
        this.selectedIds[id] = !this.selectedIds[id];
    }

    // this should be named 'selectAllFromDataInStore'
    selectAll(type?: string): void {
        //Analyze failed file items that don’t support actions should not be allowed to be selected.
        this.selectedIds = type
            ? this.ids
                  .filter((id) => this.data.get(id)[type].failed === false)
                  .reduce((accumulator, value) => {
                      return { ...accumulator, [value]: true };
                  }, {})
            : this.ids.reduce((accumulator, value) => {
                  return { ...accumulator, [value]: true };
              }, {});
    }

    selectAllPageItems(): void {
        this.ids.slice(this.skip, this.skip + this.pageSize).forEach((id) => {
            this.selectedIds[id] = true;
        });
    }

    deselectAllPageItems(): void {
        this.ids.slice(this.skip, this.skip + this.pageSize).forEach((id) => {
            this.selectedIds[id] = false;
        });
    }

    deselectAll(): void {
        this.selectedIds = {};
    }

    clearData(): void {
        this.data = new Map();
    }

    clearIds(): void {
        this.ids = [];
        this.count = 0;
    }

    clearItem(itemId: string): void {
        this.data.delete(itemId);
        this.ids = this.ids.filter((i) => i !== itemId);
        this.count = Math.max(this.count - 1, 0);
    }

    clear(): void {
        this.clearData();
        this.clearIds();
        this.deselectAll();
    }

    setSort(sortBy: string, sortDirection: SortDirection): void {
        this._updateListState({ sortBy, sortDirection });
    }

    clearSort(): void {
        this.sortBy = (this.defaultFilter.sortBy as string) || 'createdOn';
        this.sortDirection = (this.defaultFilter.sortDirection as SortDirection) || SortDirection.DESC;
    }

    setFilter(filter: any): void {
        this.filter = {
            ...this.filter,
            ...filter
        };
    }

    clearFilter(filter: string): void {
        delete this.filter[filter];

        this.filter = { ...this.filter };
    }

    clearFilterItem(filter: string, item: string): void {
        delete this.filter[filter][item];

        this.filter = {
            ...this.filter
        };

        if (Object.keys(this.filter[filter]).length === 0) {
            this.clearFilter(filter);
        }
    }

    resetFilter(): void {
        this.filter = { ...(this.defaultFilter || { search: '' }) };
    }

    clearAllFilters(type: string): void {
        if (type.toLowerCase() === 'alert') {
            this.filter = { archived: { _not_archived: true } };
        } else {
            this.filter = { search: '' };
        }
    }

    _updateIds<T>(data: T[] = [], skip = 0, idArray?: string[]): void {
        const arr = idArray || this.ids;
        arr.length = Math.min(arr.length, data.length + skip);

        for (let i = 0; i < data.length; i++) {
            if (data[i]) {
                // @ts-ignore: We can assume id exists here
                arr[i + skip] = data[i].id;
            }
        }
    }

    _updateData(data: any): void {
        if (!Array.isArray(data)) {
            data = [data];
        }

        for (const i in data) {
            let item;

            if (i === 'fileRepId') {
                item = data;
            } else {
                item = data[i];
            }

            const id = item.id;

            if (this.data.has(id)) {
                const oldItem = this.data.get(id);

                //Move item to the back of the cache if reloaded
                this.data.delete(id);
                // @ts-ignore Need to fix typing on base stores, update is guaranteed
                oldItem?.update(item);
                this.data.set(id, oldItem);
            } else {
                this.data.set(id, new this.ItemStore(item));

                // Pop the first item off the cache if cache is too large
                if (this.data.size > MAX_DATA_LENGTH) {
                    this.data.delete(this.data.keys().next().value);
                }
            }
        }
    }

    _updateCount(data: any): void {
        if (data.length === this.limit) {
            this.count += this.count == 0 ? this.limit * 2 : this.limit;
        } else {
            this.count = this.data.size;
        }
    }

    updatePageSize(newPageSize: number): void {
        this.pageSize = newPageSize;
        this.deselectAll();
    }

    updatePage(newPage: number): void {
        this.page = newPage;
    }
}
